export interface DialogOptions {
  title: string;
  content: string | HTMLElement;
  className?: string;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
}

export interface DialogButtonConfig {
  text: string;
  className?: string;
  primary?: boolean;
  handler: () => void | Promise<void>;
}

export class DialogHelper {
  private dialog!: HTMLDialogElement;
  private titleElement!: HTMLHeadingElement;
  private contentElement!: HTMLDivElement;
  private buttonContainer!: HTMLDivElement;
  private closeButton!: HTMLButtonElement;

  constructor() {
    this.createDialogFromTemplate();
    this.setupEventListeners();
  }

  private createDialogFromTemplate(): void {
    // Clone the modal template
    const template = document.getElementById('modal-template') as HTMLTemplateElement;
    const clone = template.content.cloneNode(true) as DocumentFragment;
    
    // Get references to elements
    this.dialog = clone.querySelector('.modal-dialog') as HTMLDialogElement;
    this.titleElement = clone.querySelector('.modal-title') as HTMLHeadingElement;
    this.contentElement = clone.querySelector('.modal-content') as HTMLDivElement;
    this.buttonContainer = clone.querySelector('.modal-buttons') as HTMLDivElement;
    this.closeButton = clone.querySelector('.modal-close') as HTMLButtonElement;
    
    // Add to document
    document.body.appendChild(clone);
  }

  private setupEventListeners(): void {
    // Close button handler
    this.closeButton.addEventListener('click', () => this.close());
    
    // Backdrop click handler
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.close();
      }
    });
    
    // Escape key handler
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }

  public show(options: DialogOptions, buttons: DialogButtonConfig[] = []): void {
    // Set title
    this.titleElement.textContent = options.title;
    
    // Set content (string or HTMLElement)
    if (typeof options.content === 'string') {
      this.contentElement.innerHTML = options.content;
    } else {
      this.contentElement.innerHTML = '';
      this.contentElement.appendChild(options.content);
    }
    
    // Apply custom class if provided
    if (options.className) {
      this.dialog.className = `modal-dialog ${options.className}`;
    } else {
      this.dialog.className = 'modal-dialog';
    }
    
    // Show/hide close button
    this.closeButton.style.display = options.showCloseButton !== false ? 'block' : 'none';
    
    // Clear and create buttons
    this.buttonContainer.innerHTML = '';
    buttons.forEach(buttonConfig => {
      const button = document.createElement('button');
      button.textContent = buttonConfig.text;
      button.className = buttonConfig.className || 'modal-btn';
      if (buttonConfig.primary) {
        button.classList.add('modal-btn-primary');
      }
      
      button.addEventListener('click', async () => {
        try {
          await buttonConfig.handler();
        } catch (error) {
          console.error('Dialog button handler error:', error);
        }
      });
      
      this.buttonContainer.appendChild(button);
    });
    
    // Show dialog
    this.dialog.showModal();
    
    // Focus first button or close button
    const firstButton = this.buttonContainer.querySelector('button') as HTMLButtonElement;
    if (firstButton) {
      firstButton.focus();
    } else {
      this.closeButton.focus();
    }
  }

  public close(): void {
    this.dialog.close();
  }

  public updateContent(content: string | HTMLElement): void {
    if (typeof content === 'string') {
      this.contentElement.innerHTML = content;
    } else {
      this.contentElement.innerHTML = '';
      this.contentElement.appendChild(content);
    }
  }

  public getContentElement(): HTMLDivElement {
    return this.contentElement;
  }

  public destroy(): void {
    this.dialog.remove();
  }
}